/**
 * Backgammon 盤コンポーネント。
 *
 * 入力モード (from 優先):
 *   - 自分の駒 (from) をタップ → そこから最優先 dice 値の 1 step を即実行 (「目で移動」)。
 *     候補が無ければ sel ハイライトで dest 待ちにフォールバック。
 *   - 空 dest をタップ → そこに動かせる候補が 1 つだけなら即実行。 複数あれば
 *     最優先 dice 値を採用。 0 候補なら no-op (= sel 解除)。
 *   - from から to へドラッグ → ドラッグした特定の dest 候補を実行 (経路指定)。
 *   - 残 dice タップで順序反転 (大→小 ⇔ 小→大、 既定は大が先)。
 *   - 「振った dice」 は state.round が変わるまで表示し続ける (消費済は半透明)。
 *   - bear-off 候補が複数 dice にまたがる場合は最優先 dice を採用。
 */

import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';
import {
  type BgArrow,
  type BgMove,
  type BgState,
  isBearOff,
  isEnter,
  isStep,
  type Side,
} from '../../games/backgammon';
import { useFreshLastMove } from '../../lib/first-paint';
import { useRoom } from '../../state/room';
import { getEngine } from '../../wasm';
import { BgArrowLayer } from './BgArrowLayer';

/** ダイスの目。 3x3 のどこを打つか。 数字ではなく実際の点を出す。 */
const DIE_PIPS: Record<number, boolean[]> = {
  1: [false, false, false, false, true, false, false, false, false],
  2: [true, false, false, false, false, false, false, false, true],
  3: [true, false, false, false, true, false, false, false, true],
  4: [true, false, true, false, false, false, true, false, true],
  5: [true, false, true, false, true, false, true, false, true],
  6: [true, false, true, true, false, true, true, false, true],
};

/** BG move の中継 dest (= 1 駒 step / enter / bear_off の到達点)。 enter は bar 起点なので
 *  from は無いが dest は dice 値から計算できる。 */
function moveDest(m: BgMove, side: 'First' | 'Second'): number | null {
  if (isStep(m)) return side === 'First' ? m.step.from - m.step.dice : m.step.from + m.step.dice;
  if (isEnter(m)) return side === 'First' ? 25 - m.enter.dice : m.enter.dice;
  if (isBearOff(m)) return side === 'First' ? 0 : 25;
  return null;
}

function moveFrom(m: BgMove): number | null {
  if (isStep(m)) return m.step.from;
  if (isBearOff(m)) return m.bear_off.from;
  return null; // enter は from 無し (bar から)
}

function moveDice(m: BgMove): number | null {
  if (isStep(m)) return m.step.dice;
  if (isEnter(m)) return m.enter.dice;
  if (isBearOff(m)) return m.bear_off.dice;
  return null;
}

/** ベアオフトレイを click したとき選ぶ 1 手。 BG 一般的な「ベストで上げる」 ヒューリスティック:
 *  1. dice 値 = from の正確マッチ (dist === dice) を優先 (= pip ロスなし)
 *  2. それ以外は最遠 (= 最も脱出させたい) point を、 そこに使える最小 dice で上げる
 *  3. それでも同点なら任意
 *  Side First は off=0 で dist=from、 Second は off=25 で dist=25-from。
 */
function bestBearOff(legal: BgMove[], side: 'First' | 'Second'): BgMove | null {
  const offs = legal.filter(isBearOff);
  if (offs.length === 0) return null;
  const distOf = (from: number): number => (side === 'First' ? from : 25 - from);
  const scored = offs.map((m) => {
    const from = m.bear_off.from;
    const dice = m.bear_off.dice;
    const dist = distOf(from);
    return { m, from, dice, dist, exact: dist === dice ? 1 : 0 };
  });
  scored.sort((a, b) => {
    if (a.exact !== b.exact) return b.exact - a.exact; // exact 優先
    if (a.dist !== b.dist) return b.dist - a.dist; // 遠い point 優先
    return a.dice - b.dice; // 最小 dice (oversize はもったいないので)
  });
  return scored[0].m;
}

/** OFF クリックで 可能な限り連続して bear-off する。 残 dice を全部使い切るのが正しい
 *  BG の動作 (= 1 dice 1 駒 ずつ上げる)。 各 step で bestBearOff を再評価することで
 *  「dice 6 で point 5 の駒を上げる」 等の oversize 経路もちゃんと辿る。
 *  WASM は click 時のみ。 dice 残り最大 4 (doubles) なので 4 ラウンドトリップ程度。 */
function bestBearOffChain(
  initialState: BgState,
  initialLegal: BgMove[],
): BgMove[] {
  const side = initialState.side;
  const out: BgMove[] = [];
  const engine = getEngine('Backgammon');
  let state = initialState;
  let legal = initialLegal;
  while (true) {
    const best = bestBearOff(legal, side);
    if (!best) break;
    out.push(best);
    try {
      state = engine.apply(state, best) as BgState;
    } catch {
      break;
    }
    if (state.outcome != null) break;
    if (state.side !== side) break;
    if (state.dice.length === 0) break;
    try {
      legal = engine.legal(state) as BgMove[];
    } catch {
      break;
    }
  }
  return out;
}

/** 任意の dest に 2-step (compound or twoPieces) で届く最優先候補を 1 つずつ返す。
 *  lazy 設計: click/drop 時にしか呼ばれない (= 毎 render の WASM 列挙を排除)。
 *  m1 候補は dest 到達性で事前 filter してから `engine.apply` + `engine.legal` を回すので、
 *  終盤 BG でも 1 click 数ms で済む。
 *
 *  @param fromFilter ドラッグ時の起点固定。 click 時は undefined。
 */
function find2StepFor(
  state: BgState,
  legal: BgMove[] | undefined,
  dest: number,
  dicePriorityOf: (d: number) => number,
  fromFilter?: number,
): { compound?: [BgMove, BgMove]; twoPieces?: [BgMove, BgMove] } {
  if (!legal || state.dice.length < 2) return {};
  const side = state.side;
  const sideMul = side === 'First' ? -1 : 1;
  // m1 を pre-filter: m1.dest === dest (twoPieces 候補) または m1.dest が dest まで 残ダイスで届く (compound)
  const m1Candidates = legal.filter((m) => {
    if (!(isStep(m) || isEnter(m) || isBearOff(m))) return false;
    if (fromFilter != null && moveFrom(m) !== fromFilter) return false;
    const m1d = moveDest(m, side);
    if (m1d == null) return false;
    if (m1d === dest) return true; // twoPieces 案件
    // compound: dist(m1d, dest) が state.dice - m1.dice の中にある
    const m1Dice = moveDice(m);
    if (m1Dice == null) return false;
    const remaining = [...state.dice];
    const idx = remaining.indexOf(m1Dice);
    if (idx >= 0) remaining.splice(idx, 1);
    const distSigned = (dest - m1d) * sideMul;
    if (distSigned <= 0) return false;
    if (dest === 0 || dest === 25) {
      // bear-off: dist >= 1 で 残ダイスのいずれかが dist 以上 (BG bear-off の oversize ルール)
      return remaining.some((d) => d >= distSigned);
    }
    return remaining.includes(distSigned);
  });
  if (m1Candidates.length === 0) return {};
  const engine = getEngine('Backgammon');
  const compoundCands: Array<{ seq: [BgMove, BgMove]; firstDice: number }> = [];
  const twoPiecesCands: Array<{ seq: [BgMove, BgMove]; firstDice: number }> = [];
  for (const m1 of m1Candidates) {
    let next: BgState;
    try {
      next = engine.apply(state, m1) as BgState;
    } catch {
      continue;
    }
    if (next.outcome != null) continue;
    if (next.side !== side) continue;
    let next_legal: BgMove[];
    try {
      next_legal = engine.legal(next) as BgMove[];
    } catch {
      continue;
    }
    const m1d = moveDest(m1, side);
    const firstDice = moveDice(m1) ?? 0;
    for (const m2 of next_legal) {
      if (moveDest(m2, side) !== dest) continue;
      const m2From = moveFrom(m2);
      const cand = { seq: [m1, m2] as [BgMove, BgMove], firstDice };
      if (m2From != null && m1d != null && m2From === m1d) {
        compoundCands.push(cand);
      } else if (m1d === dest) {
        twoPiecesCands.push(cand);
      }
    }
  }
  compoundCands.sort((a, b) => dicePriorityOf(a.firstDice) - dicePriorityOf(b.firstDice));
  twoPiecesCands.sort((a, b) => dicePriorityOf(a.firstDice) - dicePriorityOf(b.firstDice));
  return {
    compound: compoundCands[0]?.seq,
    twoPieces: twoPiecesCands[0]?.seq,
  };
}

interface Props {
  /** 盤 ID (= MatchState.boards[].board)。 VRF cheat banner の表示判定に使う。
   *  mini 描画では unused (= 渡さなくてよい)。 */
  board?: number;
  state: BgState;
  legal?: BgMove[];
  yourSide?: Side | null;
  /** _view.allowed_boards が現時点で着手を許可しているか。 BG は 1 ターン中 同じ side が
   *  複数回着手するゲームで engine cs と allowed_boards の整合性が特に微妙な場面 (Parallel 他盤
   *  待ち中など) があるので、 必ずこれを真の手番判定として使う。 */
  myTurnAllowed?: boolean;
  onMove?: (mv: BgMove) => void;
  mini?: boolean;
  lastMove?: BgMove;
  /** Replay / Archive 等の静的ビューでは「相手の手番」 表示を出さない。 */
  staticView?: boolean;
  /** チュートリアル用ハイライト。 各要素 `[pointNum, _]` の 1 番目が 1..24 のポイント番号
   *  (Cell 型互換のため 2 要素にしているが バックギャモンでは 1 番目のみ使用)。 */
  tutorialHighlight?: ReadonlyArray<readonly [number, number]>;
  /** 盤に重ねる矢印 (パズルの答え表示・チュートリアル)。 盤を見ている側 (yourSide) の手を描く。 */
  arrows?: BgArrow[];
}

type Selection = { kind: 'point'; point: number } | { kind: 'bar' } | null;

export function BackgammonBoard({ board, state, legal, yourSide, myTurnAllowed, onMove, mini, lastMove, staticView, tutorialHighlight, arrows }: Props) {
  const caseRef = useRef<HTMLDivElement>(null);
  // 盤を開いた瞬間は直前手を光らせない (mount 後の新しい手だけアニメする)。
  const freshLast = useFreshLastMove(lastMove);
  // VRF cheat 検出フラグ (= DO の inject_dice で proof verify が失敗した board)。
  // in-memory フラグなので、 次の round で両者が正しい proof を送り `vrf_round_ready`
  // が届くと自動で消える ([[room.ts:vrfCheatBoards]])。 mini では出さない。
  const vrfCheatActive = useRoom((s) =>
    board !== undefined && !mini && s.vrfCheatBoards.has(board),
  );
  const tutorialHintSet = new Set<number>(
    (tutorialHighlight ?? []).map(([n]) => n),
  );
  const [selected, setSelected] = useState<Selection>(null);
  // 終局後は yourSide を維持して視点反転を防ぎつつ、 入力は無効化。
  const isMyTurn =
    state.outcome == null &&
    (myTurnAllowed !== undefined
      ? myTurnAllowed
      : yourSide != null && yourSide === state.side);

  // ヒット (= 相手のブロットを bar に送った着手) を検出して bar に短い flash 演出。
  // hit を受けるのは「mover の opponent」 (= 直前手で ブロットを取られた側)。
  // mover = state.side (BG のターン中は state.side が固定)。 opp = state.side.opponent。
  // opp の bar が UI 上 top/bottom どちら側かは yourSide と opp の関係で決まる。
  const [barHitFlash, setBarHitFlash] = useState<{ at: 'top' | 'bottom'; tick: number } | null>(null);
  const lastHitRef = useRef<{ round: number; len: number } | null>(null);
  useEffect(() => {
    const hist = state.turn_history ?? [];
    const last = hist.at(-1);
    const sig = { round: state.round, len: hist.length };
    const prev = lastHitRef.current;
    const isNew = !prev || prev.round !== sig.round || prev.len !== sig.len;
    lastHitRef.current = sig;
    if (isNew && last?.hit && !mini) {
      // mover = state.side、 hit を喰った側 = opp = !state.side。
      // top bar には自分の bar 駒 (相手ホームに入るので上段)、 bottom には相手。
      const opp: 'First' | 'Second' = state.side === 'First' ? 'Second' : 'First';
      // 自分が喰らったら上段 (= 自分の bar)、 相手が喰らったら下段。
      const at: 'top' | 'bottom' = yourSide == null
        ? 'bottom'
        : opp === yourSide ? 'top' : 'bottom';
      setBarHitFlash({ at, tick: Date.now() });
      const t = setTimeout(() => setBarHitFlash(null), 320);
      return () => clearTimeout(t);
    }
  }, [state.round, state.turn_history, mini, state.side, yourSide]);

  // 「このターン振られたダイス」 を round 切替で記録する。 state.dice は
  // 消費すると縮むので、 表示用に元のロール内容を保持する。
  const [rolled, setRolled] = useState<{ round: number; dice: number[] }>({
    round: -1,
    dice: [],
  });
  useEffect(() => {
    if (state.dice.length === 0) return;
    // rolled.dice は常に「大きい方が先」。 ユーザーが diceReversed=true にすると表示順が反転する。
    if (state.round !== rolled.round) {
      setRolled({ round: state.round, dice: [...state.dice].sort((a, b) => b - a) });
    } else if (state.dice.length > rolled.dice.length) {
      // 同 round で増える = 別ロール (再振り直し)
      setRolled({ round: state.round, dice: [...state.dice].sort((a, b) => b - a) });
    }
  }, [state.round, state.dice, rolled.round, rolled.dice.length]);

  // ダイス表示順序: 標準は rolled の順、 swap で反転 (dice 値が 2 種以上のとき意味あり)。
  // 新しい round (= 新しいロール) では順序リセット。 useEffect ではなく React 公式推奨の
  // 「ref で前回値を見て render 中に setState」 パターン: useEffect の余計な fire を避ける。
  const [diceReversed, setDiceReversed] = useState(false);
  const lastRolledRoundRef = useRef(rolled.round);
  if (lastRolledRoundRef.current !== rolled.round) {
    lastRolledRoundRef.current = rolled.round;
    setDiceReversed(false);
  }

  // rolled をベースに 「消費済 / 未使用」 を計算 (state.dice にあるかで判定)。
  const diceDisplay = (() => {
    const remaining = [...state.dice];
    const items = rolled.dice.map((value) => {
      const idx = remaining.indexOf(value);
      if (idx >= 0) {
        remaining.splice(idx, 1);
        return { value, consumed: false };
      }
      return { value, consumed: true };
    });
    return diceReversed ? items.slice().reverse() : items;
  })();

  // ユーザーの 「これを先に使いたい」 順序を反映した dice の比較関数。
  // 表示で先頭にあるダイスを優先。
  const dicePriority = (d: number): number => {
    const idx = diceDisplay.findIndex((x) => x.value === d && !x.consumed);
    return idx < 0 ? 999 : idx;
  };

  // 自動確定: 自分の手番で legal が `end_turn` (+ undo) のみ = もう動かせる手は無い
  // → 短い猶予のあと自動でターン送り。 ユーザーが Undo 押せば clearTimeout で取り消し。
  //
  // **重要**: dice が空かつ turn_history も空 = 「ターン開始直後で未ロール」 (= VRF dice
  // 注入待ち) のとき、 BG engine.legal は仕様上 `[EndTurn]` だけを返す ([backgammon.rs:230]
  // の `if s.dice.is_empty()` 分岐)。 ここで auto-EndTurn を発火すると、 ダイスが振られる
  // 前に手番が opponent へ渡る → opponent も同じ局面で auto-EndTurn → ping-pong で round が
  // 際限なく進み「同じ人が指し続けるように見える」 バグになる。
  // 自分が 1 ply でも指していれば turn_history は非空なので、 「dice 全消費後の正規ターン送り」
  // と「ターン開始直後の未ロール」 を turn_history で区別できる。
  //
  // **dep は全部 primitive**: 他盤 (Parallel 進行で guest の AnimalShogi 等) の着手で snapshot
  // が更新されると `state.dice` / `legal` / `onMove` の参照が変わるため、 これらを直接 dep に
  // 入れると setTimeout(700) が並行盤の着手ごとに clearTimeout で消える → auto-EndTurn が永遠
  // に発火しなくなる。 onMove は autoEndTurnOnMoveRef (毎 render 最新化) に逃がして dep から外し、
  // legal は「auto 適格かどうか」 だけ primitive boolean 化。
  const autoEligible =
    isMyTurn &&
    !mini &&
    legal != null &&
    !legal.some((m) => m !== 'end_turn' && m !== 'undo') &&
    legal.some((m) => m === 'end_turn');
  const diceLen = state.dice?.length ?? 0;
  const historyLen = state.turn_history?.length ?? 0;
  const turnStarted = diceLen > 0 || historyLen > 0; // VRF dice 注入待ちなら false
  const autoEndTurnOnMoveRef = useRef(onMove);
  autoEndTurnOnMoveRef.current = onMove;
  useEffect(() => {
    if (!autoEligible || !turnStarted) return;
    const t = setTimeout(() => autoEndTurnOnMoveRef.current?.('end_turn'), 700);
    return () => clearTimeout(t);
  }, [autoEligible, turnStarted]);

  // 自分の駒の置かれているポイントを判別
  const ownsPoint = (p: number): boolean => {
    return state.points[p - 1]?.owner === state.side && state.points[p - 1].count > 0;
  };

  /** 候補 1 件: dest と 「使う dice 値」 のペア。 同じ dest に複数 dice が
   *  あり得る (とくに bear-off で from=3 のとき dice=3 でも 4-6 でも off 可) ので、
   *  Map ではなく配列で持つ。 表示は dice 値ラベル付き。 */
  interface Cand {
    /** dest 番号 (1..24) または 0/25 = OFF。 */
    dest: number;
    dice: number;
    move: BgMove;
  }

  const candidatesFor = (sel: Selection): Cand[] => {
    if (!sel || !legal) return [];
    const out: Cand[] = [];
    for (const m of legal) {
      if (sel.kind === 'bar') {
        if (isEnter(m)) {
          const dest = state.side === 'First' ? 25 - m.enter.dice : m.enter.dice;
          out.push({ dest, dice: m.enter.dice, move: m });
        }
      } else {
        if (isStep(m) && m.step.from === sel.point) {
          const dest =
            state.side === 'First' ? sel.point - m.step.dice : sel.point + m.step.dice;
          out.push({ dest, dice: m.step.dice, move: m });
        }
        if (isBearOff(m) && m.bear_off.from === sel.point) {
          const dest = state.side === 'First' ? 0 : 25;
          out.push({ dest, dice: m.bear_off.dice, move: m });
        }
      }
    }
    // dicePriority で並べる (表示先頭 = 優先)
    out.sort((a, b) => dicePriority(a.dice) - dicePriority(b.dice));
    return out;
  };

  const candidates = candidatesFor(selected);
  /** dest → 候補 (複数あれば最優先 dice のみ採用) */
  const candidateByDest = (() => {
    const out = new Map<number, Cand>();
    for (const c of candidates) {
      if (!out.has(c.dest)) out.set(c.dest, c);
    }
    return out;
  })();
  /** 同じ dest に複数 dice 候補があるか (= disambiguation 必要) */
  const destAmbiguous = (dest: number): boolean =>
    candidates.filter((c) => c.dest === dest).length > 1;

  // パターン B: 任意の合法手の着地先一覧 (source 未選択時の auto-source 用)
  // 同じ dest に届く源が複数あれば、 dicePriority で先頭 dice を優先。
  const moveByDest = (() => {
    const out = new Map<number, BgMove>();
    if (!legal) return out;
    type Entry = { mv: BgMove; dice: number };
    const groups = new Map<number, Entry[]>();
    for (const m of legal) {
      let dest: number | null = null;
      let dice: number | null = null;
      if (isStep(m)) {
        dest = state.side === 'First' ? m.step.from - m.step.dice : m.step.from + m.step.dice;
        dice = m.step.dice;
      } else if (isEnter(m)) {
        dest = state.side === 'First' ? 25 - m.enter.dice : m.enter.dice;
        dice = m.enter.dice;
      } else if (isBearOff(m)) {
        dest = state.side === 'First' ? 0 : 25;
        dice = m.bear_off.dice;
      }
      if (dest != null && dice != null) {
        const arr = groups.get(dest) ?? [];
        arr.push({ mv: m, dice });
        groups.set(dest, arr);
      }
    }
    for (const [dest, arr] of groups) {
      arr.sort((a, b) => dicePriority(a.dice) - dicePriority(b.dice));
      out.set(dest, arr[0].mv);
    }
    return out;
  })();

  // 2 dice 経路の解決は **lazy** に行う ([find2StepFor] 参照)。
  //   compound: 1 駒が中継経由で着地 (A → mid → dest)。 = drag 用
  //   twoPieces: 2 駒が同じ dest に着地 (A → dest, B → dest)。 = empty クリック用
  // 旧実装は毎 render で legal 全要素について `engine.apply` + `engine.legal` の WASM 往復を
  // 走らせており、 終盤の BG で他盤 broadcast 毎に ~150ms ブロックして「操作が効かない」
  // 症状になっていた。 これらは handlePointClick / tryDrop でしか consume されないので、
  // クリック/ドロップ時の lazy 計算に置換 ([useLegalMoves] とセットで対応)。

  // 「動かせる駒の置かれている point」 (= legal の from 一覧)。 sel 前にこの
  // point の駒だけハイライトする (= 「ここの駒は動かせる」 を視覚的に示す)。
  const movableSources = (() => {
    const out = new Set<number>();
    if (!legal || !isMyTurn) return out;
    for (const m of legal) {
      if (isStep(m)) out.add(m.step.from);
      else if (isBearOff(m)) out.add(m.bear_off.from);
      // enter は bar から → bg-bar 側でハイライトする
    }
    return out;
  })();
  const barMovable: boolean =
    isMyTurn && !!legal && legal.some(isEnter);
  // バーに駒があるが入場できない (fanned)。 Enter 候補ゼロ + 残ダイス有り or
  // legal が EndTurn のみ。 ユーザーへ「入場不可・ターン送り」 を明示するため
  // visual blocked + EndTurn 強調で扱う。
  const barBlocked: boolean =
    isMyTurn &&
    state.bar[state.side === 'First' ? 0 : 1] > 0 &&
    !!legal &&
    !legal.some(isEnter);

  /**
   * 直前手を指したのはどちら側か。
   *
   * **BG の `state.side` は EndTurn でしか反転しない** (engine: `BgMove::EndTurn` →
   * `side.opponent()`)。 1 ターンに何着手もするゲームなので、 チェッカーを動かす手
   * (Step / Enter / BearOff) の直後は **state.side が指した本人のまま**。 ここを
   * 「apply 後だから逆」 と決め打ちしていたため、 直前手のリングが反対方向の点に付き、
   * 自分の手なのに相手の手として色が付いていた (2026-09-19)。
   */
  const lastActorSide: 'First' | 'Second' | null = (() => {
    if (!lastMove) return null;
    if (isStep(lastMove) || isEnter(lastMove) || isBearOff(lastMove)) return state.side;
    // EndTurn / Undo は side を動かすので、 直前に指したのは今の手番の相手。
    return state.side === 'First' ? 'Second' : 'First';
  })();

  // 直前の手の起点・終点を判別 (リング表示用)。 到達点の計算は moveDest に寄せる
  // (ここで式を書き直すと、 上の move 実行側と符号がずれる)。
  const lastFromTo = (() => {
    const none = { from: undefined as undefined | number, to: undefined as undefined | number };
    if (!lastMove || mini || !lastActorSide) return none;
    if (isStep(lastMove)) {
      return { from: lastMove.step.from, to: moveDest(lastMove, lastActorSide) ?? undefined };
    }
    if (isEnter(lastMove)) {
      return { from: undefined, to: moveDest(lastMove, lastActorSide) ?? undefined };
    }
    if (isBearOff(lastMove)) {
      // 上がりの着地点は盤の外なので、 出発点だけ印を付ける。
      return { from: lastMove.bear_off.from, to: undefined };
    }
    return none;
  })();
  // 直前手の actor が自分か相手か。 yourSide=null (観戦) のときは null。
  const lastActorMine: boolean | null =
    yourSide != null && lastActorSide != null ? lastActorSide === yourSide : null;

  // 入力モード (from 優先):
  //  - 自分の駒タップ → そこから最優先 dice の手を即実行 (動けなければ sel)
  //  - 空 dest タップ → auto-source で 1 着手 (dicePriority 優先)
  //  - sel 中 (= 動けない from を選択した状態) → dest タップで具体的な経路を選ぶ
  //  - from→to ドラッグ → 候補 dest 一覧から該当を実行 (handleDrop 経由)
  const handlePointClick = (p: number) => {
    if (!isMyTurn || mini) return;

    // sel 中 + その dest にマッチした候補 → 確定
    const target = candidateByDest.get(p);
    if (selected && target) {
      onMove?.(target.move);
      setSelected(null);
      return;
    }

    // バーに駒がある場合は board 着手不可、 enter のみ。 1 候補なら即実行。
    if (state.bar[state.side === 'First' ? 0 : 1] > 0) {
      const enterCands = candidatesFor({ kind: 'bar' });
      if (enterCands.length === 0) {
        // fanned (entry blocked)。 board タップは EndTurn と同義扱い。
        if (legal?.some((m) => m === 'end_turn')) {
          onMove?.('end_turn');
        }
        setSelected(null);
        return;
      }
      // 一通りなら (= Enter 候補が 1 つだけ) どこを tap しても実行。 これによりバー駒が
      // ある場合、 ユーザーは「とりあえずどこか触る」 だけで強制的な 1 手を進められる。
      if (enterCands.length === 1) {
        onMove?.(enterCands[0].move);
        setSelected(null);
        return;
      }
      // p が enter dest 候補なら実行
      const hit = enterCands.find((c) => c.dest === p);
      if (hit) {
        onMove?.(hit.move);
        setSelected(null);
        return;
      }
      // それ以外は bar sel (dest クリック待ち)
      setSelected({ kind: 'bar' });
      return;
    }

    // 自分の駒 → そこから最優先 dice の手を即実行 (= 「fromクリック=目で移動」)
    if (ownsPoint(p)) {
      const cands = candidatesFor({ kind: 'point', point: p });
      if (cands.length > 0) {
        // candidatesFor は dicePriority 順にソート済み。 先頭 = 最優先 dice。
        onMove?.(cands[0].move);
        setSelected(null);
        return;
      }
      // この from からは動けない → sel して dest 待ち (= ユーザーへ「動かせない」 を示す)
      setSelected({ kind: 'point', point: p });
      return;
    }

    // 空 dest or 相手駒 → 「2 駒同 dest 着地 → 1 dice 単手 → compound 中継」 の順で
    //   1 click 操作を試す。 2-step 系は lazy 計算 ([find2StepFor]) で click 時のみ WASM 呼出
    //   (~5-20ms)。 これにより毎 render の WASM 列挙ブロックを排除する。
    const step2 = find2StepFor(state, legal, p, dicePriority);
    if (step2.twoPieces) {
      // 例: 8 と 10 に駒があり dice [3,5] で両方 5 に動ける → 両 dice 消費する 1 click 操作
      onMove?.(step2.twoPieces[0]);
      onMove?.(step2.twoPieces[1]);
      setSelected(null);
      return;
    }
    const auto = moveByDest.get(p);
    if (auto) {
      onMove?.(auto);
      setSelected(null);
      return;
    }
    if (step2.compound) {
      // 1 駒 compound (中継) で届く → 1 click で連続 2 着手
      onMove?.(step2.compound[0]);
      onMove?.(step2.compound[1]);
      setSelected(null);
      return;
    }

    setSelected(null);
  };

  const handleBarClick = () => {
    if (!isMyTurn || mini) return;
    if (state.bar[state.side === 'First' ? 0 : 1] === 0) return;

    const enterCands = candidatesFor({ kind: 'bar' });
    if (enterCands.length === 0) {
      // 入場不可 (fanned)。 EndTurn 以外打てないので 1 click でターン送り。
      if (legal?.some((m) => m === 'end_turn')) {
        onMove?.('end_turn');
      }
      setSelected(null);
      return;
    }
    if (enterCands.length === 1) {
      onMove?.(enterCands[0].move);
      setSelected(null);
      return;
    }
    // 複数候補 (enter で異なる dest) → sel して dest 選択
    setSelected({ kind: 'bar' });
  };

  const handleOffClick = () => {
    if (!isMyTurn || mini) return;
    const target = state.side === 'First' ? 0 : 25;
    // sel 中の from から OFF へ届く具体的候補があればそれを優先 (= ユーザー指定の駒だけ 1 手)
    if (selected) {
      const c = candidateByDest.get(target);
      if (c) {
        onMove?.(c.move);
        setSelected(null);
        return;
      }
    }
    // sel なし: 残 dice を全部使い切るまで連続でベアオフ。 BG では bear-off 可能なら
    // 全 dice を bear-off で消費するのが正しい (= 1 dice 1 駒上げ)。
    if (legal) {
      const chain = bestBearOffChain(state, legal);
      for (const m of chain) onMove?.(m);
      if (chain.length > 0) setSelected(null);
    }
  };

  // ─── ドラッグ対応 ───
  // pointerdown で from を ref に記録 → window 全体で pointerup を 1 回だけ拾う。
  // pointerup 時の position から elementFromPoint で dest を特定 (= 点・OFF・bar)。
  // pointer capture は使わない (= 元素子の onPointerUp が常に呼ばれるとは限らないため)。
  // 同じ point 内で finish したら click 扱い (handlePointClick が走る)。
  const dragFromRef = useRef<number | null>(null);
  const [dragFromVisible, setDragFromVisible] = useState<number | null>(null);

  // 最新値参照のために refs に最新の handler を持たせる (window listener が
  // dragFromRef set 時の closure に縛られないように)。
  const stateRef = useRef(state);
  const legalRef = useRef(legal);
  const isMyTurnRef = useRef(isMyTurn);
  const dicePriorityRef = useRef(dicePriority);
  const onMoveRef = useRef(onMove);
  useEffect(() => {
    stateRef.current = state;
    legalRef.current = legal;
    isMyTurnRef.current = isMyTurn;
    dicePriorityRef.current = dicePriority;
    onMoveRef.current = onMove;
  });

  const tryDrop = (from: number, dest: number) => {
    if (from === dest) return;
    if (!isMyTurnRef.current || mini) return;
    const lg = legalRef.current;
    if (!lg) return;
    const st = stateRef.current;
    // from→dest の単 dice 候補
    for (const m of lg) {
      if (isStep(m) && m.step.from === from) {
        const d = st.side === 'First' ? from - m.step.dice : from + m.step.dice;
        if (d === dest) {
          onMoveRef.current?.(m);
          setSelected(null);
          return;
        }
      }
      if (isBearOff(m) && m.bear_off.from === from) {
        const offDest = st.side === 'First' ? 0 : 25;
        if (offDest === dest) {
          onMoveRef.current?.(m);
          setSelected(null);
          return;
        }
      }
    }
    // 複合 (2 dice) で from→dest が成立するか — lazy 列挙 (from で絞り込むので ~1-3 WASM コールで済む)
    const found = find2StepFor(st, lg, dest, dicePriorityRef.current, from);
    if (found.compound) {
      onMoveRef.current?.(found.compound[0]);
      onMoveRef.current?.(found.compound[1]);
      setSelected(null);
    }
  };

  const handlePointerDown = (p: number, _e: ReactPointerEvent) => {
    if (!isMyTurn || mini) return;
    if (!ownsPoint(p)) return;
    dragFromRef.current = p;
    setDragFromVisible(p);

    const onUp = (ev: PointerEvent) => {
      window.removeEventListener('pointerup', onUp);
      const from = dragFromRef.current;
      dragFromRef.current = null;
      setDragFromVisible(null);
      if (from == null) return;
      const target = document.elementFromPoint(ev.clientX, ev.clientY);
      if (!target) return;
      // dest 種別を hit-test。 .bg-point[data-point-abs] / .bg-off / .bg-bar
      const pointEl = (target as Element).closest('[data-point-abs]') as HTMLElement | null;
      if (pointEl) {
        const abs = parseInt(pointEl.dataset.pointAbs ?? '', 10);
        if (!Number.isNaN(abs)) tryDrop(from, abs);
        return;
      }
      const offEl = (target as Element).closest('[data-off-abs]') as HTMLElement | null;
      if (offEl) {
        const abs = parseInt(offEl.dataset.offAbs ?? '', 10);
        if (!Number.isNaN(abs)) tryDrop(from, abs);
        return;
      }
    };
    window.addEventListener('pointerup', onUp);
  };

  // 旧 onPointerUp は不要 (window で拾う)。 互換のため空関数は残さず削除。

  // 描画レイアウトは常に "自分のホーム = 右下" になるよう、Guest 視点では
  // 盤を 180° 回転 (上下行入れ替え)。 さらに点番号は相対座標で見せる
  // (各プレイヤーが「自分の 1〜6 は自分のホーム」 と認識できる)。
  // 表示ラベルが rel、 board state lookup 用が abs。
  const flip = yourSide === 'Second';
  const toAbs = (rel: number): number => (flip ? 25 - rel : rel);

  // 表示ラベル: 上段 = 13-24 (相手領域)、 下段 = 12-1 (自分の領域 + ホーム右下)
  const topLabels = [13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];
  const botLabels = [12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1];

  // bar / off は 「自分=下、相手=上」 として一貫させる
  const youIdx = yourSide === 'Second' ? 1 : 0;
  const oppIdx = 1 - youIdx;
  const youBar = state.bar[youIdx];
  const oppBar = state.bar[oppIdx];
  const youOff = state.off[youIdx];
  const oppOff = state.off[oppIdx];
  // 自分の bear-off 候補 = (Host: abs 0, Guest: abs 25)。 ボタンは 「自分の OFF」 = bottom。
  const youOffKey = yourSide === 'Second' ? 25 : 0;

  // 候補表示: selected のときは候補のみ強い色 + dice 値ラベル。
  //   選択前は dest を弱くハイライトしない (= 駒側を movable で示すため)。
  const isCandidate = (abs: number): boolean => {
    if (selected) return candidateByDest.has(abs);
    return false;
  };

  // 候補の dice ラベル (selected のとき only)。 ambiguous なら "3/5" 等。
  const candidateLabel = (abs: number): string | null => {
    if (!selected) return null;
    const matching = candidates.filter((c) => c.dest === abs);
    if (matching.length === 0) return null;
    return matching.map((c) => c.dice).join('/');
  };

  // 与えられた相対ラベルから候補・選択・last 状態を判定
  const renderPoint = (rel: number, isTop: boolean) => {
    const abs = toAbs(rel);
    return (
      <BgPointView
        key={rel}
        n={rel}
        point={state.points[abs - 1]}
        yourSide={yourSide ?? null}
        top={isTop}
        cand={isCandidate(abs)}
        movable={!selected && movableSources.has(abs)}
        candDiceLabel={candidateLabel(abs)}
        candAmbiguous={destAmbiguous(abs)}
        sel={selected?.kind === 'point' && selected.point === abs}
        last={lastFromTo.from === abs || lastFromTo.to === abs}
        lastMine={lastActorMine}
        freshLast={freshLast}
        onClick={() => handlePointClick(abs)}
        onPointerDown={(e) => handlePointerDown(abs, e)}
        pointAbs={abs}
        dragSource={dragFromVisible === abs}
        tutorialHint={tutorialHintSet.has(rel)}
      />
    );
  };

  const showOppHint = !mini && !staticView && !isMyTurn && state.outcome == null;

  return (
    <div className={`bg-frame ${mini ? 'mini' : ''} ${showOppHint ? 'not-my-turn' : ''}`}>
      {vrfCheatActive && (
        <div
          className="bg-vrf-cheat"
          data-testid="bg-vrf-cheat-detected"
          role="alert"
        >
          相手のダイスが不正の可能性があります
        </div>
      )}
      {/* 盤のケース。 焼いた 1 枚の画像 (枠・バー・受け・24 の点・手前の小口) の上に
          grid を重ねる。 列幅は焼いたときの mm 比とそのまま対応させてあるので、
          点の三角形と .bg-point の当たり判定が一致する。 → styles.css の Backgammon */}
      <div className="bg-case" ref={caseRef}>
      {arrows && arrows.length > 0 && !mini && (
        <BgArrowLayer caseRef={caseRef} state={state} arrows={arrows} yourSide={yourSide ?? 'First'} />
      )}
      {/* 上段 (相手側): 各プレイヤーから見て 13-24 番 (= 相対座標) */}
      <div className="bg-row bg-row-top">
        {topLabels.slice(0, 6).map((rel) => renderPoint(rel, true))}
        {/* bar の駒は「これから入る側」 (= 相手陣) に置く。 自分の駒は相手のホームに
            入るので上段、 相手の駒は自分のホームに入るので下段。 */}
        <div
          key={`bar-top-${barHitFlash?.at === 'top' ? barHitFlash.tick : 'idle'}`}
          className={`bg-bar bg-bar-top ${barMovable ? 'movable' : ''} ${barBlocked ? 'blocked' : ''} ${selected?.kind === 'bar' ? 'sel' : ''} ${barHitFlash?.at === 'top' ? 'hit-flash' : ''}`}
          data-testid="bg-bar-self"
          onClick={handleBarClick}
        >
          <BgBarStack
            count={youBar}
            side={yourSide === 'Second' ? 'Second' : 'First'}
            mine={yourSide == null ? null : true}
          />
        </div>
        {topLabels.slice(6).map((rel) => renderPoint(rel, true))}
        <div className="bg-off">
          <span className="bg-off-label">ゴール</span>
          <span className="bg-off-count">{oppOff}</span>
        </div>
      </div>

      {/* 下段 (自分側): 12-7 番 + 自分のホーム 6-1 + OFF */}
      <div className="bg-row bg-row-bot">
        {botLabels.slice(0, 6).map((rel) => renderPoint(rel, false))}
        <div
          key={`bar-bot-${barHitFlash?.at === 'bottom' ? barHitFlash.tick : 'idle'}`}
          className={`bg-bar bg-bar-bot ${barHitFlash?.at === 'bottom' ? 'hit-flash' : ''}`}
          onClick={handleBarClick}
        >
          <BgBarStack
            count={oppBar}
            side={yourSide === 'Second' ? 'First' : 'Second'}
            mine={yourSide == null ? null : false}
            bot
          />
        </div>
        {botLabels.slice(6).map((rel) => renderPoint(rel, false))}
        <div
          className={`bg-off ${candidateByDest.has(youOffKey) ? 'cand' : ''} ${destAmbiguous(youOffKey) ? 'cand-ambig' : ''}`}
          onClick={handleOffClick}
          data-off-abs={youOffKey}
        >
          <span className="bg-off-label">ゴール</span>
          <span className="bg-off-count">{youOff}</span>
          {candidateLabel(youOffKey) && (
            <span className="bg-cand-label" aria-hidden>
              {candidateLabel(youOffKey)}
            </span>
          )}
        </div>
        {/* dice: 実物と同じく**自分の右半分**に振る。 下段の行 (position:relative) の
              上端 = 盤の縦中央なので、 そこを基準に半分ぶん持ち上げて中央に置く。
              相手の番は向かいの席から振るので左半分 (CSS の .opp)。
              dice タップで順序反転、 ボタン側はクリックが外へ propagate しないよう stopPropagation。 */}
        {!mini && (diceDisplay.length > 0 || isMyTurn) && (
          <div className={`bg-dicebar ${state.side === 'First' ? 'side-host' : 'side-guest'} ${yourSide && state.side === yourSide ? 'mine' : 'opp'}`}>
            {diceDisplay.length > 0 && (
              <div
                className="bg-dice"
                data-testid="bg-dice"
                onClick={() => {
                  if (state.dice.length >= 2) setDiceReversed((v) => !v);
                }}
                title="タップで順序反転"
              >
                {diceDisplay.map((d, i) => {
                  const isNext =
                    !d.consumed &&
                    i === diceDisplay.findIndex((x) => !x.consumed);
                  return (
                    <span
                      key={i}
                      className={`bg-die ${d.consumed ? 'consumed' : 'active'} ${isNext ? 'next' : ''}`}
                      data-die={d.value}
                      aria-label={`ダイス ${d.value}`}
                    >
                      {/* 目は数字ではなく実際のダイスの点。 3x3 のどこを打つかだけ CSS に渡す
                          (§3.6 物は焼く / 情報は焼かない — 象牙の立方体は焼いた画像が持つ) */}
                      <span className="bg-die-pips" aria-hidden>
                        {DIE_PIPS[d.value]?.map((on, p) => (
                          <i key={p} className={on ? 'on' : ''} />
                        ))}
                      </span>
                    </span>
                  );
                })}
              </div>
            )}
            {isMyTurn && (
              <div className="bg-actions">
                {/* Undo は **ターン途中のみ** 出す: 全 dice 消費後の auto-EndTurn 待ち中は
                    押せるように見えてしまうが、 ここで undo すると「待ち時間中に取り消す」 という
                    奇妙な UX になる。 autoEligible && turnStarted (= EndTurn が 700ms 後に
                    発火する状態) では非表示にして「ターンは終わった」 ことを視覚的にも伝える。 */}
                {(state.turn_history?.length ?? 0) > 0 && !(autoEligible && turnStarted) && (
                  <button
                    className="bg-action-btn bg-undo"
                    data-testid="bg-undo"
                    onClick={(e) => {
                      e.stopPropagation();
                      onMove?.('undo');
                    }}
                    title="1 手戻す"
                    aria-label="1 手戻す"
                  >
                    ↺
                  </button>
                )}
                {/* 確定ボタンは廃止: dice 全消費 / 詰みで自動 EndTurn (上の useEffect 参照)。 */}
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
    </div>
  );
}

function BgPointView({
  n,
  point,
  yourSide,
  top,
  cand,
  movable,
  candDiceLabel,
  candAmbiguous,
  sel,
  last,
  lastMine,
  freshLast,
  onClick,
  onPointerDown,
  pointAbs,
  dragSource,
  tutorialHint,
}: {
  n: number;
  point: { owner: Side | null; count: number };
  yourSide: Side | null;
  top?: boolean;
  cand?: boolean;
  /** 自分が動かせる駒が置かれている point (legal の from に含まれる) */
  movable?: boolean;
  candDiceLabel?: string | null;
  candAmbiguous?: boolean;
  sel?: boolean;
  last?: boolean;
  lastMine?: boolean | null;
  /** mount 後に来た新しい直前手か (false なら光らせない)。 */
  freshLast?: boolean;
  onClick: () => void;
  onPointerDown?: (e: ReactPointerEvent) => void;
  /** 絶対座標 1..24 (window pointerup での hit test に使う)。 */
  pointAbs: number;
  /** ドラッグ中の元の point か (= 視覚的に持ち上がる) */
  dragSource?: boolean;
  tutorialHint?: boolean;
}) {
  const sideCls = point.owner === 'First' ? 'host' : 'guest';
  const ownerCls =
    yourSide == null || point.owner == null
      ? '' // spectator: 伝統色 (host/guest) フォールバック
      : point.owner === yourSide
        ? 'mine'
        : 'opp';
  const lastCls = !last
    ? ''
    : `${lastMine == null ? 'last' : lastMine ? 'last last-mine' : 'last last-opp'}${freshLast ? '' : ' no-anim'}`;
  // 5 個より多いとき、 「最後の 1 個」 (= 5 個目) に総数を書く。
  // それ未満は普通の checker を 5 個まで表示。
  const visible = Math.min(point.count, 5);
  return (
    <button
      className={`bg-point ${top ? 'top' : 'bot'} ${cand ? 'cand' : ''} ${movable ? 'movable' : ''} ${sel ? 'sel' : ''} ${lastCls} ${n % 2 === 0 ? 'dark' : 'light'} ${candAmbiguous ? 'cand-ambig' : ''} ${dragSource ? 'dragging' : ''} ${tutorialHint ? 'tutorial-hint' : ''}`}
      onClick={onClick}
      onPointerDown={onPointerDown}
      aria-label={`point ${n}`}
      data-point={n}
      data-point-abs={pointAbs}
    >
      <span className="bg-point-num">{n}</span>
      <div className="bg-stack">
        {Array.from({ length: visible }).map((_, i) => {
          const isLastWithCount = point.count > 5 && i === visible - 1;
          // 駒は厚みぶん下へ射影された小口を持つので、 積むと隣の駒に食い込む。
          // **手前 (画面下) の駒が上に来る**よう z を振る。 top 列は後ろの子ほど下、
          // bot 列は column-reverse で後ろの子ほど上なので逆順になる。
          return (
            <span
              key={i}
              className={`bg-checker ${sideCls} ${ownerCls} ${isLastWithCount ? 'count' : ''}`}
              style={{ zIndex: top ? i : visible - i }}
            >
              {isLastWithCount && <span className="bg-checker-num">{point.count}</span>}
            </span>
          );
        })}
      </div>
      {candDiceLabel && (
        <span className="bg-cand-label" aria-hidden>
          {candDiceLabel}
        </span>
      )}
    </button>
  );
}

function BgBarStack({
  count,
  side,
  mine,
  bot,
}: {
  count: number;
  side: Side;
  /** null = spectator (伝統色のまま)。 true = 自分。 false = 相手。 */
  mine: boolean | null;
  /** 画面下側の bar (= 自分側)。 積む向きが逆なので駒の前後関係も逆になる。 */
  bot?: boolean;
}) {
  if (count === 0) return null;
  const sideCls = side === 'First' ? 'host' : 'guest';
  const ownerCls = mine == null ? '' : mine ? 'mine' : 'opp';
  // 4 個より多いとき、 4 個目に「総数」 を書いて以降は省く (バーは点より狭い)。
  const visible = Math.min(count, 4);
  return (
    <div className={`bg-bar-stack ${sideCls}`} data-testid={`bar-${side.toLowerCase()}`}>
      {Array.from({ length: visible }).map((_, i) => {
        const showCount = count > 4 && i === visible - 1;
        return (
          <span
            key={i}
            className={`bg-checker ${sideCls} ${ownerCls} ${showCount ? 'count' : ''}`}
            style={{ zIndex: bot ? visible - i : i }}
          >
            {showCount && <span className="bg-checker-num">{count}</span>}
          </span>
        );
      })}
    </div>
  );
}
